import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { getCachedAt, getNewestCachedAt } from '../utils/cacheFreshness';

/**
 * Marks a view as showing cached rather than live data.
 *
 * Required on anything financial: those endpoints are cached so they can be printed
 * without a connection, so an offline statement can show a balance that is no longer
 * current. The stamp is rendered inside the printable area deliberately — a printed
 * page outlives the screen it came from, and whoever reads it later has no other way
 * to tell it wasn't live.
 *
 * @param {string}   [endpoint]  API path this view's data came from (most precise)
 * @param {string[]} [prefixes]  path prefixes to timestamp instead, for views drawing
 *                               on several endpoints where naming one would be arbitrary
 */
export default function StaleDataNotice({ endpoint, prefixes }) {
  const online = useOnlineStatus();
  const { t } = useTranslation();
  const [cachedAt, setCachedAt] = useState(null);

  // `prefixes` is an array literal at most call sites, so depend on its contents
  // rather than its identity or the effect would re-run on every render.
  const prefixKey = Array.isArray(prefixes) ? prefixes.join('|') : '';

  useEffect(() => {
    let cancelled = false;
    if (online || (!endpoint && !prefixKey)) {
      setCachedAt(null);
      return undefined;
    }
    const lookup = endpoint
      ? getCachedAt(endpoint)
      : getNewestCachedAt(prefixKey.split('|').filter(Boolean));
    lookup.then((when) => {
      if (!cancelled) setCachedAt(when);
    });
    return () => { cancelled = true; };
  }, [endpoint, prefixKey, online]);

  if (online) return null;

  const when = cachedAt ? cachedAt.toLocaleString() : null;

  return (
    <Box sx={{ my: 1, '@media print': { display: 'block' } }}>
      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {when
            ? t('offlineWrites.asOf', { when })
            : t('offlineWrites.notLive')}
        </Typography>
      </Alert>
    </Box>
  );
}
